import type { KohaMentionItem } from "~/components/commentNodeKoha.types";
import { getPreviousSiblingIgnoringEmptyTextNodes } from "~/units/dom";

/**
 * Transform all HTML elements that represent notes.
 *
 * @param window
 *   The window object. In a browser, just use `window`. In Node, use something like JSDOM.
 * @param body
 *   The body DOM.
 * @param nid
 *   We need a node ID to make HTML IDs unique.
 * @param kohaMentionItems
 *   The Koha mention items in the order in which they appear in the body.
 *
 * @return
 *   An array of note info with which we can build a bibliography.
 */
export function transformNotes(
  window: Window & typeof globalThis,
  body: HTMLElement,
  nid: number,
  kohaMentionItems: KohaMentionItem[],
): NoteData[] {
  const noteElements = findAllNoteElements(window, body);

  trimWhitespaceBeforeNotes(noteElements);

  const notes: NoteData[] = [];

  let s = 1; // Superscript's ordinal
  let n = 1; // Note's ordinal
  let curSup: HTMLElement; // The current superscript being used
  let supId = `comment-${nid}-sup-${s}`; // The ID of the current superscript

  // Set the current Superscript to a default value
  curSup = window.document.createElement("sup");
  curSup.setAttribute("id", supId);

  noteElements.forEach((node) => {
    let note: NoteData;

    // Extract data from the field item.
    if (isOldStyleKohaMention(node)) {
      // TODO: Remove this after `lw_mention_*` fields have been inlined into the body field.
      const delta: number = +(node.dataset.delta ?? -1);
      note = {
        type: "koha",
        item: kohaMentionItems[delta] || {
          error: "Missing reference",
          biblio: -1,
        },

        // Filled in later:
        noteId: "",
        supId: "",
      };
    } else if (isKohaMention(node)) {
      note = {
        type: "koha",
        item: {
          biblio: +(node.dataset.biblio ?? -1),
          pages: node.dataset.pages,
          prefix: node.dataset.prefix,
          rendered: node.dataset.rendered,
        },

        // Filled in later:
        noteId: "",
        supId: "",
      };
    } else if (isUncatalogedMention(node)) {
      note = {
        type: "uncataloged",
        item: { markup: node.innerHTML || "Missing reference" },

        // Filled in later:
        noteId: "",
        supId: "",
      };
    } else if (isArbitraryNote(node)) {
      note = {
        type: "arbitrary",
        item: { markup: node.innerHTML },

        // Filled in later:
        noteId: "",
        supId: "",
      };
    } else {
      // The current node should not be added to the list of notes.
      return;
    }

    const noteId = `comment-${nid}-ref-${n}-note`;

    // Check if the previous element is a superscript tag.
    const previousSiblingIgnoringEmptyTextNodes =
      getPreviousSiblingIgnoringEmptyTextNodes(node);
    const previousElementIsSup =
      previousSiblingIgnoringEmptyTextNodes &&
      (previousSiblingIgnoringEmptyTextNodes as Element)?.tagName === "SUP";

    if (!previousElementIsSup) {
      // Create the next superscript
      supId = `comment-${nid}-sup-${s}`;
      curSup = window.document.createElement("sup");
      curSup.setAttribute("id", supId);

      // Insert the superscript into the DOM before the current node
      node.parentNode?.insertBefore(curSup, node);

      // Increment Superscript ordinal
      s++;
    }

    // Add the node to the current superscript
    curSup.appendChild(node);

    // If the node has a previous sibling
    // AND the previous sibling is a DOM element
    if (node.previousSibling && node.previousSibling.nodeType === 1) {
      // Add a comma in between the current node and its previous sibling
      node.previousSibling.after(",");
    }

    // Create a link to the note.
    const link = window.document.createElement("a");
    link.title = `Show note ${n}`;
    link.textContent = n.toString();
    link.href = `#${noteId}`;
    link.dataset.note = n.toString();

    // Replace the current node with the link.
    node.replaceWith(link);

    // Add the current node to the notes.
    note.noteId = noteId;
    note.supId = supId;
    notes.push(note);

    // Increment note ordinal
    n++;
  });

  return notes;
}

/**
 * Find all known elements that represent notes.
 *
 * @param window
 *   The window object. In a browser, just use `window`. In Node, use something like JSDOM.
 * @param dom
 *   The body DOM.
 */
function findAllNoteElements(
  window: Window & typeof globalThis,
  dom: HTMLElement,
): HTMLElement[] {
  return [
    // TODO: Remove `[data-field]` after `lw_mention_*` fields have been inlined into the body field.
    ...dom.querySelectorAll(
      "[data-field], span.koha-reference, span.note, span.uncataloged",
    ),
  ].filter((node): node is HTMLElement => {
    return (
      node instanceof window.HTMLElement &&
      // TODO: Remove the following conditions after `lw_mention_*` fields have been inlined into the body field:
      (isOldStyleKohaMention(node) ||
        isKohaMention(node) ||
        isUncatalogedMention(node) ||
        isArbitraryNote(node))
    );
  });
}

/**
 * Remove whitespace from the end of all text nodes that directly precede a note element.
 *
 * @param elements
 *   An array of HTML elements representing notes.
 */
function trimWhitespaceBeforeNotes(elements: HTMLElement[]) {
  elements.forEach((node) => {
    const previous = node.previousSibling;
    if (previous && previous.nodeType === 3) {
      // The previous sibling is a text node.
      previous.nodeValue = previous.nodeValue
        ? previous.nodeValue.trimEnd()
        : previous.nodeValue;
    }
  });
}

function isOldStyleKohaMention(element: HTMLElement) {
  // TODO: Remove this after `lw_mention_*` fields have been inlined into the body field.
  return element.dataset.field === "lw_mention_koha";
}

function isKohaMention(element: HTMLElement) {
  return [...element.classList].includes("koha-reference");
}

function isUncatalogedMention(element: HTMLElement) {
  return [...element.classList].includes("uncataloged");
}

function isArbitraryNote(element: HTMLElement) {
  return [...element.classList].includes("note");
}

export type NoteData =
  | KohaMentionNoteData
  | UncatalogedMentionNoteData
  | ArbitraryNoteData;

interface NoteDataCommon {
  noteId: string;
  supId: string;
}

interface KohaMentionNoteData extends NoteDataCommon {
  type: "koha";
  item: KohaMentionItem;
}

interface UncatalogedMentionNoteData extends NoteDataCommon {
  type: "uncataloged";
  item: { markup: string };
}

interface ArbitraryNoteData extends NoteDataCommon {
  type: "arbitrary";
  item: { markup: string };
}
